// A transport acknowledgement is not a persisted clinical record. Keep this
// guard at the API boundary so an older worker/proxy cannot manufacture IDs.
export function getClinicalRecordId(record) {
  const value = record?.referenceId ?? record?.ReferenceId ?? record?.id ?? record?.Id;
  return value != null && !['', 'undefined', 'null'].includes(String(value)) ? value : null;
}

// The legacy visit endpoints require numeric IDs; canonical adapters accept
// either form and resolve a reference-only device record before network use.
export function getClinicalRouteId(record) {
  return record?.id ?? record?.Id ?? getClinicalRecordId(record);
}

export function requireClinicalSaveResult(data, { requireId = false, resource = 'record', identity } = {}) {
  if (data?.queued || data?.status === 'saved-on-device' || data?.savedOnDevice) {
    const error = new Error('Saved on this device. Reconnect and review this change before saving it to the chart.');
    error.code = 'CLINICAL_SAVE_UNCONFIRMED';
    error.savedOnDevice = true;
    error.offlineDraftId = data.draftId || null;
    throw error;
  }
  if (requireId && !(identity || getClinicalRecordId(data))) {
    const error = new Error(`The ${resource} save could not be confirmed. Keep this form open and check the server record before retrying.`);
    error.code = 'CLINICAL_SAVE_UNCONFIRMED';
    throw error;
  }
  return data;
}

export function isUncertainClinicalSave(error) {
  return Boolean(error?.savedOnDevice || error?.deviceSaveFailed
    || error?.code === 'CLINICAL_SAVE_UNCONFIRMED'
    || error instanceof TypeError
    || [408, 425, 429].includes(error?.status)
    || error?.status >= 500);
}

export function clinicalSaveErrorMessage(error, fallback = 'The change could not be saved.') {
  if (error?.deviceSaveFailed) return 'Not saved on device. Keep this form open and reconnect before leaving.';
  if (error?.savedOnDevice) return 'Saved on device. Reconnect and review before saving to the server.';
  return error?.message || fallback;
}

export function newClinicalOperationId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (!globalThis.crypto?.getRandomValues) throw new Error('Secure device storage is unavailable. Reconnect before charting.');
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}
